import type { BookmarkListItem } from 'markcat-domain';
import styles from './BookmarkBadges.module.css';

interface BookmarkBadgesProps {
  bookmark: BookmarkListItem;
}

export interface Badge {
  label: string;
  variant: string;
  /** Broken / Dead / low-confidence / terminal-failure Failed — the states a
   *  tile must surface even at rest. Informational badges (PDF, bot,
   *  title-only, in-flight status) stay hover-only in the tiles overlay. */
  serious?: boolean;
}

/** The badge set for a bookmark, in render order. Shared by the list/timeline
 *  row (full set) and the tiles corner overlay (serious always, rest on hover). */
export function computeBadges(bookmark: BookmarkListItem): Badge[] {
  const badges: Badge[] = [];

  // Status badges. In-flight states are informational; the two terminal
  // failures (invalid, classification_failed) are serious so tiles surface
  // them at rest and fold them into the Open-button accessible name.
  if (bookmark.status === 'new' || bookmark.status === 'processing') {
    badges.push({ label: 'Processing', variant: 'info' });
  } else if (bookmark.status === 'extracted' || bookmark.status === 'pending_classification') {
    badges.push({ label: 'Classifying', variant: 'info' });
  } else if (bookmark.status === 'invalid' || bookmark.status === 'classification_failed') {
    badges.push({ label: 'Failed', variant: 'error', serious: true });
  } else if (bookmark.status === 'needs_subcategory') {
    badges.push({ label: 'Refining', variant: 'info' });
  } else if (
    bookmark.status === 'needs_browser' ||
    bookmark.status === 'needs_stealth' ||
    bookmark.status === 'needs_scraper'
  ) {
    badges.push({ label: 'Retrying', variant: 'info' });
  }

  // PDF badge
  if (bookmark.content_type === 'application/pdf' || bookmark.pdf_pages) {
    const pages = bookmark.pdf_pages ? ` (${bookmark.pdf_pages}p)` : '';
    badges.push({ label: `PDF${pages}`, variant: 'neutral' });
  }

  // Confidence badge
  if (bookmark.confidence != null && bookmark.confidence < 0.7 && bookmark.status === 'categorized') {
    badges.push({ label: `${Math.round(bookmark.confidence * 100)}%`, variant: 'warning', serious: true });
  }

  // Broken link / dead
  if (bookmark.is_broken_link === 1) {
    badges.push({ label: 'Broken', variant: 'error', serious: true });
  }
  if (bookmark.is_dead === 1) {
    badges.push({ label: 'Dead', variant: 'error', serious: true });
  }

  // Bot protected
  if (bookmark.is_bot_protected === 1) {
    badges.push({ label: 'Bot protected', variant: 'warning' });
  }

  // Title-only classification
  if (bookmark.extraction_method === 'title_only') {
    badges.push({ label: 'Title only', variant: 'neutral' });
  }

  return badges;
}

export function BookmarkBadges({ bookmark }: BookmarkBadgesProps) {
  const badges = computeBadges(bookmark);
  if (badges.length === 0) return null;

  return (
    <div className={styles.badges}>
      {badges.map((badge, i) => (
        <span key={i} className={`${styles.badge} ${styles[badge.variant]}`}>
          {badge.label}
        </span>
      ))}
    </div>
  );
}
